import MarkdownIt from 'markdown-it';
import type Renderer from 'markdown-it/lib/renderer.mjs';
import type Token from 'markdown-it/lib/token.mjs';
// @ts-expect-error - no types available
import markdownItTaskLists from 'markdown-it-task-lists';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { VISION_AI_SUMMARY_INTERACTIONS } from '@/components/VisionAi/constants';
import { parseMessageSegments } from '@/components/VisionAi/utils/message';
import type {
  VisionAiWidgetClickPayload,
  VisionWidget,
} from '@/components/VisionAi/widgets/visionWidget.types';
import { RESOURCE_CENTER_BASE_URL } from '@/config';

export type GroupedSegment =
  | { type: 'markdown'; content: string }
  | { type: 'widget-group'; ids: string[] };

export type AssistantMessageMarkdownProps = {
  text?: string;
  widgets?: VisionWidget[];
};

function createMarkdownIt(): MarkdownIt {
  const md = new MarkdownIt({
    linkify: true,
    typographer: true,
    html: true,
  }).use(markdownItTaskLists, { enabled: true });

  const previousHeadingOpen = md.renderer.rules.heading_open;
  const previousHeadingClose = md.renderer.rules.heading_close;
  const previousParagraphOpen = md.renderer.rules.paragraph_open;
  const previousBlockquoteOpen = md.renderer.rules.blockquote_open;
  const previousBulletListOpen = md.renderer.rules.bullet_list_open;
  const previousOrderedListOpen = md.renderer.rules.ordered_list_open;
  const previousListItemOpen = md.renderer.rules.list_item_open;

  const HEADING_TYPO_CLASS: Record<string, string> = {
    h2: 'typo-h2',
    h3: 'typo-h3',
    h4: 'typo-h4',
    h5: 'typo-h5',
  };

  function pushTokenClass(token: Token, className: string) {
    const current = token.attrGet('class');
    token.attrSet('class', current ? `${current} ${className}` : className);
  }

  function normalizeHeadingToken(tokens: Token[], idx: number) {
    const tag = tokens[idx].tag;
    if (tag === 'h1') {
      tokens[idx].tag = 'h2';
    } else if (tag === 'h6') {
      tokens[idx].tag = 'h5';
    }
  }

  md.renderer.rules.heading_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    normalizeHeadingToken(tokens, idx);
    const tag = tokens[idx].tag;
    const typo = HEADING_TYPO_CLASS[tag];
    pushTokenClass(tokens[idx], 'mb-2 mt-4');
    if (typo) {
      pushTokenClass(tokens[idx], typo);
    }
    if (previousHeadingOpen) {
      return previousHeadingOpen(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.paragraph_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    pushTokenClass(tokens[idx], 'typo-base mb-2');
    if (previousParagraphOpen) {
      return previousParagraphOpen(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.blockquote_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    pushTokenClass(
      tokens[idx],
      'typo-base text-subtle not-italic border-l-2 border-(--color-split-pipe-tertiary) pl-4',
    );
    if (previousBlockquoteOpen) {
      return previousBlockquoteOpen(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.bullet_list_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    const listToken = tokens[idx];
    if ((listToken.attrGet('class') ?? '').includes('contains-task-list')) {
      pushTokenClass(listToken, 'list-none');
    }
    pushTokenClass(listToken, 'mb-2 pl-5');
    if (previousBulletListOpen) {
      return previousBulletListOpen(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.ordered_list_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    const listToken = tokens[idx];
    if ((listToken.attrGet('class') ?? '').includes('contains-task-list')) {
      pushTokenClass(listToken, 'list-none');
    }
    pushTokenClass(listToken, 'mb-2 pl-5');
    if (previousOrderedListOpen) {
      return previousOrderedListOpen(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.list_item_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    const itemToken = tokens[idx];
    pushTokenClass(itemToken, 'typo-base');
    if ((itemToken.attrGet('class') ?? '').includes('task-list-item')) {
      pushTokenClass(itemToken, 'list-none');
    }
    if (previousListItemOpen) {
      return previousListItemOpen(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.hr = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string => {
    pushTokenClass(
      tokens[idx],
      'mx-auto my-4 h-px w-1/3 max-w-full shrink-0 border-0 bg-(--color-split-pipe-tertiary)',
    );
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.heading_close = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    env: unknown,
    self: Renderer,
  ): string => {
    normalizeHeadingToken(tokens, idx);
    if (previousHeadingClose) {
      return previousHeadingClose(tokens, idx, options, env, self);
    }
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.link_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string => {
    tokens[idx].attrPush(['target', '_blank']);
    tokens[idx].attrPush(['rel', 'noopener']);
    pushTokenClass(tokens[idx], 'text-action');
    return self.renderToken(tokens, idx, options);
  };

  /**
   * Markdown tables — three-element scaffold so wide tables can horizontally
   * scroll without trapping the sticky <th> row:
   *
   *   .vision-table-pan       ← outer; no overflow → sticky escapes to SidePanel
   *     .vision-table-track   ← overflow-x: clip, overflow-y: visible (clip is
   *                             the only non-visible value that doesn't create
   *                             a sticky containing block)
   *       <table>             ← shifted via transform: translateX(-scrollLeft)
   *     .vision-table-scroll  ← native horizontal scrollbar (proxy, JS-synced),
   *                             sticky to bottom of SidePanel viewport above
   *                             the SidePanel footer.
   *       .vision-table-spacer
   *
   * `useMarkdownTableScrollbars` wires the scroll → transform sync and
   * shows/hides the proxy based on actual overflow.
   */
  md.renderer.rules.table_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string => {
    pushTokenClass(tokens[idx], 'w-full border-separate border-spacing-0');
    return `<div class="vision-table-pan my-4 w-full mb-2"><div class="vision-table-track overflow-x-clip overflow-y-visible">${self.renderToken(tokens, idx, options)}`;
  };

  md.renderer.rules.table_close = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string =>
    `${self.renderToken(tokens, idx, options)}</div><div class="vision-table-scroll sticky bottom-(--side-panel-footer-height,0px) z-20 overflow-x-auto h-3 hidden bg-background-secondary"><div class="vision-table-spacer h-px"></div></div></div>`;

  md.renderer.rules.thead_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string => self.renderToken(tokens, idx, options);

  md.renderer.rules.th_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string => {
    pushTokenClass(
      tokens[idx],
      'sticky top-(--side-panel-header-height,0px) z-10 h-10 min-w-[100px] border-b border-solid border-bd-light bg-background-secondary pl-2 text-left align-middle typo-base-semibold',
    );
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.td_open = (
    tokens: Token[],
    idx: number,
    options: Parameters<Renderer['renderToken']>[2],
    _env: unknown,
    self: Renderer,
  ): string => {
    pushTokenClass(
      tokens[idx],
      'h-10 min-w-[100px] border-b border-solid border-bd-light pl-2 align-middle typo-base',
    );
    return self.renderToken(tokens, idx, options);
  };

  return md;
}

/**
 * Vision AI assistant markdown: MarkdownIt, segments, and click tracking.
 * Used by {@link AssistantMessageSidePanelContent}
 */
export function useAssistantMessageMarkdown(
  props: Readonly<AssistantMessageMarkdownProps>,
  emitMessageInteraction: (payload: VisionAiWidgetClickPayload) => void,
) {
  const router = useRouter();
  const route = useRoute();
  const markdown = createMarkdownIt();

  const widgetMap = computed(() => {
    const map = new Map<string, VisionWidget>();
    (props.widgets ?? []).forEach((widget) => {
      if (widget.id) map.set(widget.id, widget);
    });
    return map;
  });

  const segments = computed(() => parseMessageSegments(props.text ?? '', widgetMap.value));

  const groupedSegments = computed((): GroupedSegment[] => {
    const result: GroupedSegment[] = [];
    let currentGroup: { type: 'widget-group'; ids: string[] } | null = null;

    for (const segment of segments.value) {
      if (segment.type === 'widget') {
        if (!currentGroup) {
          currentGroup = { type: 'widget-group', ids: [] };
        }
        currentGroup.ids.push(segment.id);
      } else if (segment.type === 'markdown') {
        if (currentGroup && segment.content.trim() === '') {
          continue;
        }
        if (currentGroup) {
          result.push(currentGroup);
          currentGroup = null;
        }
        result.push({ type: 'markdown', content: segment.content });
      }
    }

    if (currentGroup) {
      result.push(currentGroup);
    }

    return result;
  });

  function renderMarkdown(content: string): string {
    return markdown.render(content);
  }

  function getLinkInteractionType(href: string): string | null {
    try {
      const { pathname, origin } = new URL(href, window.location.origin);

      if (origin !== window.location.origin) {
        if (href.startsWith(RESOURCE_CENTER_BASE_URL)) {
          return VISION_AI_SUMMARY_INTERACTIONS.RESOURCE_CENTRE_CLICK;
        }
        return VISION_AI_SUMMARY_INTERACTIONS.WEB_CLICK;
      }

      let resolved = router.resolve(pathname);
      if (!resolved.name && route.params.brandLabel) {
        resolved = router.resolve(`/${route.params.brandLabel}${pathname}`);
      }

      const routeName = resolved.name ? String(resolved.name) : null;
      if (!routeName) return null;
      if (routeName.endsWith('.tweets') || routeName === 'library.media') {
        return VISION_AI_SUMMARY_INTERACTIONS.POST_CLICK;
      }
      if (routeName.startsWith('community.messages')) {
        return VISION_AI_SUMMARY_INTERACTIONS.COMMENT_CLICK;
      }
    } catch {
      // ignore invalid URLs
    }
    return null;
  }

  function handleMarkdownClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest('a');
    if (!link) return;
    const interaction = getLinkInteractionType(link.href);
    if (interaction) emitMessageInteraction({ interaction });
  }

  return {
    widgetMap,
    groupedSegments,
    renderMarkdown,
    handleMarkdownClick,
  };
}
